import { useState } from "react";
import { TENANT_DOMAIN } from "../../../utils/domain";
import { ERROR_MESSAGES, ITEMS_PER_PAGE } from "../../../constant/constant";
import { useAxios } from "../../../utils/useAxios";

/**
 * @typedef {object} StudentReportItem
 * @property {string} username - Username of the student
 * @property {string} name - Name of the student
 * @property {string} email - Email of the student
 * @property {string} lessons - Lessons completed (e.g., "0/0")
 * @property {number} avg_score - Average score
 * @property {string} status - Student status (e.g., "complete")
 */

/**
 * @typedef {object} ClassroomReportInfo
 * @property {number} id - Classroom ID
 * @property {string} name - Classroom name
 * @property {number} total_students - Total students
 * @property {number} total_lessons - Total lessons
 * @property {number} total_quests - Total quests
 * @property {number} avg_score - Average score
 * @property {number} highest_score - Highest score
 * @property {number} lowest_score - Lowest score
 * @property {string} top_student - Top student
 * @property {string} least_student - Least student
 */

/**
 * @typedef {object} StudentsPaginated
 * @property {StudentReportItem[]} data - Array of student report items
 * @property {number} current_page - Current page number
 * @property {number} per_page - Number of items per page
 * @property {number} total - Total number of items
 * @property {number} last_page - Last page number
 */

/**
 * @typedef {object} ClassroomReportResponse
 * @property {ClassroomReportInfo} classroom - Classroom details
 * @property {StudentsPaginated} students - Paginated list of students
 */

/**
 * @typedef {object} GetClassroomReportParams
 * @property {number | string} classroomId - Classroom ID (required)
 */

/**
 * Custom hook to fetch the classroom report by ID for teacher
 */
export const useGetClassroomReport = () => {
  const [report, setReport] = useState(
    /** @type {ClassroomReportResponse | null | undefined} */ (undefined),
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const { axiosInstance } = useAxios();

  /**
   * Fetch individual classroom report by ID
   * @async
   * @param {GetClassroomReportParams} params
   * @param {number} [params.page=1] - Page number for student list pagination
   * @param {number} [params.per_page] - Number of students per page
   */
  const getClassroomReport = async ({
    classroomId,
    page = 1,
    per_page = ITEMS_PER_PAGE,
  } = {}) => {
    try {
      setLoading(true);
      setError("");

      const url = `${TENANT_DOMAIN}/classroom/${classroomId}/report`;

      const params = {};
      if (page) params.page = page;
      if (per_page) params.per_page = per_page;

      const response = await axiosInstance.get(url, { params });

      setReport(response?.data?.data || null);
    } catch (err) {
      setReport(null);
      if (err?.response?.status === 500) {
        setError(ERROR_MESSAGES.INTERNAL_SERVER);
      } else {
        setError(err?.response?.data?.message || ERROR_MESSAGES.UNKNOWN);
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    report,
    loading,
    error,
    getClassroomReport,
    setReport,
  };
};
